/**
 * WorkflowService - Handles workflow rule API operations
 */
export class WorkflowService {
  constructor(apiClient) {
    this.apiClient = apiClient;
    console.log('WorkflowService initialized');
  }

  /**
   * Fetch all workflow rules
   * @returns {Promise<Array>} Array of WorkflowRule objects
   */
  async fetchWorkflowRules() {
    return this.apiClient.get('/api/workflows/fetch');
  }

  /**
   * Fetch a single workflow rule by ID
   * @param {string} ruleId - The workflow rule ID
   * @returns {Promise<Object>} WorkflowRule object
   */
  async fetchWorkflowRule(ruleId) {
    return this.apiClient.get(`/api/workflow/${ruleId}/fetch`);
  }

  /**
   * Create a new workflow rule
   * @param {Object} request - WorkflowRuleRequest object
   * @returns {Promise<void>}
   */
  async createWorkflowRule(request) {
    return this.apiClient.post('/api/workflow/create', request);
  }

  /**
   * Update an existing workflow rule
   * @param {Object} update - WorkflowRuleUpdate object
   * @returns {Promise<void>}
   */
  async updateWorkflowRule(update) {
    return this.apiClient.post('/api/workflow/update', update);
  }

  /**
   * Delete a workflow rule
   * @param {string} ruleId - The workflow rule ID
   * @returns {Promise<void>}
   */
  async deleteWorkflowRule(ruleId) {
    return this.apiClient.post(`/api/workflow/${ruleId}/delete`);
  }

  /**
   * Fetch workflow logs for a specific rule within a date range
   * @param {number} from - Epoch millis timestamp
   * @param {number} to - Epoch millis timestamp
   * @returns {Promise<Array>} Array of WorkflowLog objects
   */
  async fetchWorkflowLogs(ruleId, from, to) {
    console.log('[WorkflowService] fetchWorkflowLogs called with:', { ruleId, from, to });
    console.log('[WorkflowService] from type:', typeof from, 'to type:', typeof to);
    
    const url = `/api/workflow/${ruleId}/logs/fetch?from=${from}&to=${to}`;
    
    console.log('[WorkflowService] Calling GET', url);
    
    try {
      const response = await this.apiClient.get(url);
      console.log('[WorkflowService] Response received:', response);
      return response;
    } catch (error) {
      console.error('[WorkflowService] Request failed:', error);
      console.error('[WorkflowService] Error response:', error.response);
      throw error;
    }
  }

  /**
   * Fetch workflow logs for a specific document
   * @param {string} documentId - The document ID
   * @returns {Promise<Array>} Array of WorkflowLog objects
   */
  async fetchWorkflowLogsForDocument(documentId, options = {}) {
    return this.apiClient.get(`/api/workflow/logs/document/${documentId}/fetch`, options);
  }

  /**
   * Fetch all workflow logs within a date range
   * @param {string} from - ISO 8601 timestamp (Instant)
   * @param {string} to - ISO 8601 timestamp (Instant)
   * @returns {Promise<Array>} Array of WorkflowLog objects
   */
  async fetchAllWorkflowLogs(from, to) {
    return this.apiClient.get('/api/workflow/logs/fetch', {
      params: { from, to }
    });
  }
}
